import React, { useEffect, useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, Lock, CreditCard, Check, X, ChevronDown, Star, ArrowRight, Clock, AlertTriangle } from 'lucide-react';
// --- Subcomponents ---
const CountdownTimer = () => {
  const [timeLeft, setTimeLeft] = useState({
    h: 23,
    m: 59,
    s: 49
  });
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        let {
          h,
          m,
          s
        } = prev;
        if (s > 0) {
          s--;
        } else {
          s = 59;
          if (m > 0) {
            m--;
          } else {
            m = 59;
            if (h > 0) {
              h--;
            }
          }
        }
        return {
          h,
          m,
          s
        };
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  return <div className="flex gap-3 md:gap-4 justify-center my-6" data-id="element-0">
      {[{
      label: 'Horas',
      value: timeLeft.h
    }, {
      label: 'Min',
      value: timeLeft.m
    }, {
      label: 'Seg',
      value: timeLeft.s
    }].map((unit, i) => <div key={i} className="flex flex-col items-center" data-id="element-1">
          <div className="bg-[#111] border border-[#E89BAA]/30 text-[#FADCE2] text-3xl md:text-5xl font-bold rounded-xl w-16 h-16 md:w-24 md:h-24 flex items-center justify-center shadow-lg font-['Inter']" data-id="element-2">
            {String(unit.value).padStart(2, '0')}
          </div>
          <span className="text-[#E89BAA] text-[10px] md:text-xs uppercase mt-2 font-semibold tracking-widest" data-id="element-3">
            {unit.label}
          </span>
        </div>)}
    </div>;
};
const FaqItem = ({
  question,
  answer
}: {
  question: string;
  answer: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border-b border-[#E89BAA]/30 last:border-0" data-id="element-4">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full py-6 flex justify-between items-center text-left focus:outline-none" data-id="element-5">
        <span className="font-['Inter'] font-semibold text-lg text-[#0A0A0A] pr-4" data-id="element-6">
          {question}
        </span>
        <motion.div animate={{
        rotate: isOpen ? 180 : 0
      }} transition={{
        duration: 0.2
      }} data-id="element-7">
          <ChevronDown className="w-6 h-6 text-[#E89BAA]" data-id="element-8" />
        </motion.div>
      </button>
      <AnimatePresence data-id="element-9">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3,
        ease: 'easeInOut'
      }} className="overflow-hidden" data-id="element-10">
            <p className="pb-6 text-[#0A0A0A]/80 font-['Inter'] leading-relaxed" data-id="element-11">
              {answer}
            </p>
          </motion.div>}
      </AnimatePresence>
    </div>;
};
const FadeIn = ({
  children,
  delay = 0
}: {
  children: React.ReactNode;
  delay?: number;
}) => <motion.div initial={{
  opacity: 0,
  y: 30
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-50px'
}} transition={{
  duration: 0.6,
  delay,
  ease: 'easeOut'
}} data-id="element-12">
    {children}
  </motion.div>;
// --- Checkout URLs ---
const CHECKOUT_BASE = 'https://www.blibliotecadigital.com/checkouts/cn/hWNCKVNHwqk9XtA9HAtqz71m/es-ar?_r=AQABpa4_8xSn2ulutsanMFlp3PPvPZNkiW7Wl1w8oflbzkc&cart_link_id=mTUVMisr&preview_theme_id=152474878124';
const CHECKOUT_ESCALA = 'https://www.blibliotecadigital.com/checkouts/cn/hWNCKVoTnghqwk9d38VAEIza/es-ar?_r=AQABaD3HgIM3i66W_PJrdSJFOMByRcJ427vmk6Dbx3GSpzY&cart_link_id=ERO1wHNQ&preview_theme_id=152474878124';
// --- Main Page Component ---
export function App() {
  return <div className="min-h-screen bg-[#FFF8FA] font-['Inter'] selection:bg-[#FADCE2] selection:text-[#0A0A0A]" data-id="element-13">
      {/* Sticky Urgency Bar */}
      <div className="sticky top-0 z-50 bg-[#0A0A0A] text-[#FADCE2] py-2 px-4 text-xs md:text-sm font-medium flex justify-center items-center gap-4 md:gap-8 border-b border-[#E89BAA]/20" data-id="element-14">
        <span className="flex items-center gap-1.5" data-id="element-15">
          <Shield className="w-4 h-4" data-id="element-16" /> Garantía 7 días
        </span>
        <span className="flex items-center gap-1.5" data-id="element-17">
          <Lock className="w-4 h-4" data-id="element-18" /> Pago seguro
        </span>
        <span className="flex items-center gap-1.5" data-id="element-19">
          <CreditCard className="w-4 h-4" data-id="element-20" /> Mercado Pago
        </span>
      </div>

      {/* HERO SECTION */}
      <section className="bg-[#0A0A0A] text-[#FFF8FA] pt-12 pb-20 px-4 overflow-hidden relative" data-id="element-21">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-[#FADCE2]/5 rounded-full blur-[120px] pointer-events-none" data-id="element-22"></div>
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-center relative z-10" data-id="element-23">
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.8
        }} data-id="element-24">
            <p className="text-[#E89BAA] font-bold tracking-widest uppercase text-sm mb-4 flex items-center gap-2" data-id="element-25">
              <span className="text-xl" data-id="element-26">💅</span> AURA BIT™
            </p>
            <h1 className="font-['Playfair_Display'] text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.1] mb-6" data-id="element-27">
              Todo tu año de manicura.
              <br data-id="element-28" />
              <span className="text-[#FADCE2] italic font-normal" data-id="element-29">
                Listo. Desde hoy.
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-[#FFF8FA]/80 mb-8 font-light leading-relaxed" data-id="element-30">
              Megapack Manicura Listo — Sistema completo para manicuras
              argentinas
            </p>

            <div className="flex flex-wrap gap-3 mb-10" data-id="element-31">
              {['📄 100% PDF', '⚡ Acceso en 5 min', '🇦🇷 Hecho para Argentina', '💅 6 Tomos + 6 Bonos'].map((chip, i) => <span key={i} className="bg-[#111] border border-[#E89BAA]/30 text-[#FADCE2] px-4 py-2 rounded-full text-sm font-medium" data-id="element-32">
                  {chip}
                </span>)}
            </div>

            <motion.a href={CHECKOUT_BASE} target="_blank" rel="noopener noreferrer" whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} className="w-full md:w-auto bg-[#FADCE2] text-[#0A0A0A] px-8 py-5 rounded-full font-bold text-lg md:text-xl shadow-[0_0_40px_rgba(250,220,226,0.3)] hover:shadow-[0_0_60px_rgba(250,220,226,0.5)] transition-all flex items-center justify-center gap-3" data-id="element-33">
              💅 QUIERO MI MEGAPACK AHORA
            </motion.a>
            <div className="mt-4 flex items-center justify-center md:justify-start gap-4 text-sm text-[#FFF8FA]/50" data-id="element-34">
              <span className="flex items-center gap-1" data-id="element-35">
                <Shield className="w-4 h-4" data-id="element-36" /> Garantía 7 días
              </span>
              <span className="flex items-center gap-1" data-id="element-37">
                <Lock className="w-4 h-4" data-id="element-38" /> Pago seguro
              </span>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="relative" data-id="element-39">
            <img src="https://cdn.magicpatterns.com/uploads/bXCAss6SGbxQFentCh6LnP/heylloyd-image-1779162796586.png" alt="Megapack Manicura Listo" className="w-full max-w-lg mx-auto drop-shadow-[0_20px_50px_rgba(232,155,170,0.15)]" data-id="element-40" />
          </motion.div>
        </div>
      </section>

      {/* STATS STRIP */}
      <div className="bg-[#FADCE2] text-[#0A0A0A] py-5 px-4 border-y border-[#E89BAA]/30" data-id="element-41">
        <div className="max-w-6xl mx-auto flex flex-wrap justify-center gap-6 md:gap-16 font-semibold text-sm md:text-base" data-id="element-42">
          <span className="flex items-center gap-2" data-id="element-43">
            👥 +500 manicuras confían
          </span>
          <span className="flex items-center gap-2" data-id="element-44">📚 6 Tomos listos</span>
          <span className="flex items-center gap-2" data-id="element-45">🎁 6 Bonos incluidos</span>
          <span className="flex items-center gap-2" data-id="element-46">⭐ 4.9/5 Calificación</span>
        </div>
      </div>

      {/* TESTIMONIALS 1 */}
      <section className="py-20 px-4 bg-[#FFF8FA]" data-id="element-47">
        <div className="max-w-6xl mx-auto grid md:grid-cols-3 gap-6" data-id="element-48">
          {[{
          name: 'Valentina G.',
          loc: 'Córdoba',
          text: 'Organicé todo el año en una tarde. Por fin sé exactamente cuánto gano.'
        }, {
          name: 'Romina M.',
          loc: 'Buenos Aires',
          text: 'Mis turnos están llenos hace 3 meses seguidos. Nunca me había pasado.'
        }, {
          name: 'Laura P.',
          loc: 'Rosario',
          text: 'Los guiones de WhatsApp solos ya valen todo. Convierto el doble de consultas.'
        }].map((t, i) => <FadeIn key={i} delay={i * 0.1} data-id="element-49">
              <div className="bg-white p-8 rounded-3xl shadow-sm border border-[#FADCE2]/50 h-full" data-id="element-50">
                <div className="flex gap-1 mb-4" data-id="element-51">
                  {[...Array(5)].map((_, j) => <Star key={j} className="w-5 h-5 fill-[#E89BAA] text-[#E89BAA]" data-id="element-52" />)}
                </div>
                <p className="text-lg font-['Playfair_Display'] italic text-[#0A0A0A] mb-6" data-id="element-53">
                  "{t.text}"
                </p>
                <p className="font-semibold text-[#0A0A0A]" data-id="element-54">
                  — {t.name},{' '}
                  <span className="text-[#E89BAA] font-normal" data-id="element-55">{t.loc}</span>
                </p>
              </div>
            </FadeIn>)}
        </div>
      </section>

      {/* PROBLEM SECTION */}
      <section className="py-24 px-4 bg-[#FFF8FA] relative" data-id="element-56">
        <div className="max-w-4xl mx-auto" data-id="element-57">
          <FadeIn data-id="element-58">
            <h2 className="font-['Playfair_Display'] text-4xl md:text-5xl font-bold text-center mb-8 text-[#0A0A0A]" data-id="element-59">
              ¿Cuántos domingos más vas a perder así?
            </h2>
            <div className="text-lg md:text-xl text-[#0A0A0A]/80 space-y-6 text-center mb-12 font-light" data-id="element-60">
              <p data-id="element-61">Sos manicura. Amás lo que hacés.</p>
              <p data-id="element-62">
                Pero nadie te dijo que ibas a pasar los domingos sin saber qué
                publicar, con la agenda floja sin entender por qué, cobrando a
                ojo sin saber si ganás o perdés, y respondiendo consultas sin
                saber cómo cerrarlas.
              </p>
              <p className="font-semibold text-[#E89BAA]" data-id="element-63">
                No es falta de talento. Es falta de un sistema.
              </p>
            </div>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-64">
            <div className="bg-[#0A0A0A] rounded-3xl p-8 md:p-12 shadow-xl transform rotate-1 hover:rotate-0 transition-transform duration-500" data-id="element-65">
              <div className="space-y-6" data-id="element-66">
                {['Agenda floja sin saber qué hacer para llenarla', 'Precios calculados a ojo — sin saber si ganás de verdad', 'Sin saber qué publicar en redes ni cuándo', 'Clientas que preguntan precio y no vuelven a escribir', 'Cada turno diferente — sin protocolo, sin consistencia', 'El mismo ciclo, el próximo domingo'].map((item, i) => <div key={i} className="flex items-start gap-4" data-id="element-67">
                    <div className="bg-red-500/20 p-1 rounded-full mt-1 shrink-0" data-id="element-68">
                      <X className="w-5 h-5 text-red-400" data-id="element-69" />
                    </div>
                    <p className="text-[#FFF8FA]/90 text-lg line-through decoration-red-500/50" data-id="element-70">
                      {item}
                    </p>
                  </div>)}
              </div>
            </div>
          </FadeIn>

          <FadeIn delay={0.4} data-id="element-71">
            <p className="text-center text-2xl md:text-3xl font-['Playfair_Display'] italic font-bold text-[#0A0A0A] mt-16" data-id="element-72">
              ¿Y si este fuera el último domingo que pasás así?
            </p>
          </FadeIn>
        </div>
      </section>

      {/* SOLUTION REVEAL */}
      <section className="bg-[#0A0A0A] text-[#FADCE2] py-24 px-4 text-center border-t border-[#E89BAA]/20" data-id="element-73">
        <FadeIn data-id="element-74">
          <div className="inline-block bg-[#111] border border-[#E89BAA]/30 px-6 py-2 rounded-full mb-8" data-id="element-75">
            <span className="text-xl" data-id="element-76">🏆</span> MEGAPACK MANICURA LISTO
          </div>
          <h2 className="font-['Playfair_Display'] text-5xl md:text-7xl font-bold mb-6" data-id="element-77">
            6 Tomos + 6 Bonos
          </h2>
          <p className="text-2xl md:text-3xl font-light italic text-[#E89BAA]" data-id="element-78">
            Todo resuelto.
          </p>
        </FadeIn>
      </section>

      {/* 6 TOMOS SECTION */}
      <section className="bg-[#0A0A0A] py-20 px-4" data-id="element-79">
        <div className="max-w-6xl mx-auto" data-id="element-80">
          <FadeIn data-id="element-81">
            <h2 className="font-['Playfair_Display'] text-4xl md:text-5xl font-bold text-center text-[#FFF8FA] mb-16" data-id="element-82">
              📚 LOS 6 TOMOS
            </h2>
          </FadeIn>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-20" data-id="element-83">
            {[{
            num: '1',
            icon: '📘',
            title: 'Administración & Finanzas',
            desc: 'Precios reales, gastos, inventario y control total del dinero'
          }, {
            num: '2',
            icon: '📗',
            title: 'Gestión de Clientas & Atención',
            desc: 'Fichas, fidelización, protocolo y seguimiento post turno'
          }, {
            num: '3',
            icon: '📙',
            title: 'Redes Sociales 365',
            desc: '100 ideas de posts, reels, calendarios y textos listos para todo el año'
          }, {
            num: '4',
            icon: '📕',
            title: 'Marketing & Ventas',
            desc: 'Guiones de venta, manejo de objeciones y promos que funcionan'
          }, {
            num: '5',
            icon: '📓',
            title: 'Catálogo de Servicios',
            desc: 'Diseños por temporada, asesoramiento y cómo cobrar el nail art'
          }, {
            num: '6',
            icon: '📔',
            title: 'Planificador Anual Completo',
            desc: 'Plan mes a mes, trackers de facturación, clientas y mensajes listos'
          }].map((tomo, i) => <FadeIn key={i} delay={i * 0.1} data-id="element-84">
                <div className="bg-[#111] border border-[#E89BAA]/20 rounded-3xl p-8 hover:border-[#E89BAA]/60 transition-colors h-full flex flex-col" data-id="element-85">
                  <div className="flex justify-between items-start mb-6" data-id="element-86">
                    <span className="text-4xl" data-id="element-87">{tomo.icon}</span>
                    <span className="text-[#E89BAA] font-bold text-xl opacity-50" data-id="element-88">
                      Tomo {tomo.num}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold text-[#FADCE2] mb-4" data-id="element-89">
                    {tomo.title}
                  </h3>
                  <p className="text-[#FFF8FA]/70 leading-relaxed mt-auto" data-id="element-90">
                    {tomo.desc}
                  </p>
                </div>
              </FadeIn>)}
          </div>

          {/* Cada tomo incluye */}
          <FadeIn data-id="element-91">
            <div className="max-w-3xl mx-auto bg-[#FADCE2] rounded-3xl p-8 md:p-12 text-[#0A0A0A]" data-id="element-92">
              <h3 className="font-['Playfair_Display'] text-2xl md:text-3xl font-bold mb-8 text-center" data-id="element-93">
                Cada tomo incluye:
              </h3>
              <div className="grid sm:grid-cols-2 gap-6" data-id="element-94">
                {['Planillas y calculadoras con tus números reales', 'Textos y guiones listos para copiar y pegar', 'Checklists diarios, semanales y mensuales', 'Calendarios de contenido y promos', '100% adaptado a la realidad argentina'].map((item, i) => <div key={i} className="flex items-start gap-3" data-id="element-95">
                    <div className="bg-[#0A0A0A] p-1 rounded-full mt-1 shrink-0" data-id="element-96">
                      <Check className="w-4 h-4 text-[#FADCE2]" data-id="element-97" />
                    </div>
                    <span className="font-medium" data-id="element-98">{item}</span>
                  </div>)}
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* 6 BONOS SECTION */}
      <section className="py-24 px-4 bg-[#FFF8FA]" data-id="element-99">
        <div className="max-w-4xl mx-auto" data-id="element-100">
          <FadeIn data-id="element-101">
            <h2 className="font-['Playfair_Display'] text-4xl md:text-5xl font-bold text-center text-[#0A0A0A] mb-16" data-id="element-102">
              🎁 LOS 6 BONOS <br data-id="element-103" />
              <span className="text-[#E89BAA] italic text-3xl md:text-4xl" data-id="element-104">
                Siempre incluidos
              </span>
            </h2>
          </FadeIn>

          <div className="space-y-4 mb-12" data-id="element-105">
            {[{
            num: '01',
            title: 'Kit de Respuestas Rápidas para WhatsApp',
            desc: '50 respuestas listas para cada situación',
            val: '$8.000'
          }, {
            num: '02',
            title: 'Agenda Express para Llenar Turnos',
            desc: 'Plan de acción de 24 hs, 3 y 7 días',
            val: '$10.000'
          }, {
            num: '03',
            title: '100 Frases para Historias que Venden',
            desc: 'Publicás todos los días sin pensar',
            val: '$9.000'
          }, {
            num: '04',
            title: 'Checklist Premium de Cabina',
            desc: 'Atención consistente y profesional en cada turno',
            val: '$8.000'
          }, {
            num: '05',
            title: 'Mini Guía de Precios Rápidos',
            desc: 'Fórmula simple para cobrar lo que corresponde',
            val: '$9.000'
          }, {
            num: '06',
            title: 'Calendario de Promos y Fechas Clave',
            desc: 'Tu año comercial resuelto mes a mes',
            val: '$9.000'
          }].map((bono, i) => <FadeIn key={i} delay={i * 0.1} data-id="element-106">
                <div className="bg-white border border-[#FADCE2] rounded-2xl p-6 flex flex-col md:flex-row items-start md:items-center gap-6 shadow-sm hover:shadow-md transition-shadow" data-id="element-107">
                  <div className="bg-[#FADCE2]/50 text-[#E89BAA] font-bold text-xl w-12 h-12 rounded-full flex items-center justify-center shrink-0" data-id="element-108">
                    {bono.num}
                  </div>
                  <div className="flex-1" data-id="element-109">
                    <h4 className="text-lg font-bold text-[#0A0A0A] mb-1" data-id="element-110">
                      {bono.title}
                    </h4>
                    <p className="text-[#0A0A0A]/60 text-sm" data-id="element-111">{bono.desc}</p>
                  </div>
                  <div className="flex flex-row md:flex-col items-center md:items-end gap-3 md:gap-1 w-full md:w-auto mt-4 md:mt-0 pt-4 md:pt-0 border-t md:border-0 border-[#FADCE2]" data-id="element-112">
                    <span className="text-[#0A0A0A]/40 line-through font-medium" data-id="element-113">
                      {bono.val}
                    </span>
                    <span className="bg-[#0A0A0A] text-[#FADCE2] px-3 py-1 rounded-full text-sm font-bold tracking-wide" data-id="element-114">
                      GRATIS
                    </span>
                  </div>
                </div>
              </FadeIn>)}
          </div>

          <FadeIn data-id="element-115">
            <div className="bg-[#0A0A0A] text-center p-8 rounded-3xl text-[#FFF8FA]" data-id="element-116">
              <p className="text-lg mb-2" data-id="element-117">
                Valor total bonos{' '}
                <span className="line-through text-[#FFF8FA]/50" data-id="element-118">$53.000</span>
              </p>
              <p className="text-2xl md:text-3xl font-bold text-[#FADCE2]" data-id="element-119">
                → GRATIS con tu Megapack
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* COUNTDOWN SECTION */}
      <section className="bg-[#0A0A0A] py-16 px-4 border-y border-[#E89BAA]/30" data-id="element-120">
        <div className="max-w-3xl mx-auto text-center" data-id="element-121">
          <FadeIn data-id="element-122">
            <div className="flex items-center justify-center gap-2 text-[#E89BAA] font-bold tracking-widest uppercase mb-6" data-id="element-123">
              <Clock className="w-5 h-5" data-id="element-124" /> PRECIO DE LANZAMIENTO — CIERRA EN:
            </div>
            <CountdownTimer data-id="element-125" />
            <div className="mt-8 inline-flex items-center gap-2 bg-red-500/10 text-red-400 px-6 py-3 rounded-full font-medium border border-red-500/20" data-id="element-126">
              <AlertTriangle className="w-5 h-5" data-id="element-127" /> Después sube $8.000 ARS. No
              vuelve.
            </div>
          </FadeIn>
        </div>
      </section>

      {/* PRICING SECTION */}
      <section id="pricing" className="py-24 px-4 bg-[#FFF8FA]" data-id="element-128">
        <div className="max-w-5xl mx-auto" data-id="element-129">
          <FadeIn data-id="element-130">
            <h2 className="font-['Playfair_Display'] text-4xl md:text-5xl font-bold text-center text-[#0A0A0A] mb-16" data-id="element-131">
              ELEGÍ TU MEGAPACK
            </h2>
          </FadeIn>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto items-center" data-id="element-132">
            {/* Card 1: Base */}
            <FadeIn delay={0.1} data-id="element-133">
              <div className="bg-white border border-[#FADCE2] rounded-3xl p-8 md:p-10 shadow-lg" data-id="element-134">
                <h3 className="text-2xl font-bold text-[#0A0A0A] mb-2 flex items-center gap-2" data-id="element-135">
                  📘 MEGAPACK MANICURA LISTO
                </h3>
                <p className="text-[#0A0A0A]/60 font-medium mb-8" data-id="element-136">
                  6 Tomos + 6 Bonos
                </p>

                <div className="mb-8" data-id="element-137">
                  <span className="text-[#0A0A0A]/40 line-through text-xl block mb-1" data-id="element-138">
                    $51.000
                  </span>
                  <div className="flex items-baseline gap-2" data-id="element-139">
                    <span className="text-5xl font-bold text-[#0A0A0A]" data-id="element-140">
                      $19.990
                    </span>
                    <span className="text-xl font-bold text-[#E89BAA]" data-id="element-141">
                      ARS
                    </span>
                  </div>
                </div>

                <a href={CHECKOUT_BASE} target="_blank" rel="noopener noreferrer" className="w-full bg-[#0A0A0A] text-[#FADCE2] py-4 rounded-xl font-bold text-lg hover:bg-[#111] transition-colors flex items-center justify-center gap-2" data-id="element-142">
                  → QUIERO MI MEGAPACK
                </a>
              </div>
            </FadeIn>

            {/* Card 2: Escala (Highlighted) */}
            <FadeIn delay={0.2} data-id="element-143">
              <div className="relative bg-[#0A0A0A] border-2 border-[#E89BAA] rounded-3xl p-8 md:p-10 shadow-[0_0_40px_rgba(232,155,170,0.2)] md:-translate-y-4" data-id="element-144">
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#E89BAA] text-[#0A0A0A] px-6 py-1.5 rounded-full text-sm font-bold flex items-center gap-1.5 whitespace-nowrap" data-id="element-145">
                  <Star className="w-4 h-4 fill-current" data-id="element-146" /> MÁS ELEGIDO
                </div>

                <h3 className="text-2xl font-bold text-[#FADCE2] mb-2 flex items-center gap-2" data-id="element-147">
                  🏆 MEGAPACK + MÉTODO ESCALA
                </h3>
                <p className="text-[#FFF8FA]/70 font-medium mb-4" data-id="element-148">
                  6 Tomos + 6 Bonos + Sistema de Expansión y Escala
                </p>
                <p className="text-sm text-[#E89BAA] mb-8 bg-[#E89BAA]/10 p-3 rounded-lg border border-[#E89BAA]/20" data-id="element-149">
                  Para manicuras que quieren equipo, membresías e ingresos más
                  allá de los turnos
                </p>

                <div className="mb-8" data-id="element-150">
                  <span className="text-[#FFF8FA]/40 line-through text-xl block mb-1" data-id="element-151">
                    $69.000
                  </span>
                  <div className="flex items-baseline gap-2" data-id="element-152">
                    <span className="text-5xl font-bold text-[#FFF8FA]" data-id="element-153">
                      $27.990
                    </span>
                    <span className="text-xl font-bold text-[#E89BAA]" data-id="element-154">
                      ARS
                    </span>
                  </div>
                </div>

                <a href={CHECKOUT_ESCALA} target="_blank" rel="noopener noreferrer" className="w-full bg-[#FADCE2] text-[#0A0A0A] py-4 rounded-xl font-bold text-lg hover:bg-white transition-colors flex items-center justify-center gap-2 shadow-[0_0_20px_rgba(250,220,226,0.2)]" data-id="element-155">
                  → QUIERO EL MEGAPACK + MÉTODO ESCALA
                </a>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* METODO ESCALA COMPARISON */}
      <section className="py-16 px-4 bg-[#FFF8FA]" data-id="element-156">
        <div className="max-w-4xl mx-auto" data-id="element-157">
          <FadeIn data-id="element-158">
            <h3 className="font-['Playfair_Display'] text-3xl font-bold text-center text-[#0A0A0A] mb-10" data-id="element-159">
              ¿Qué agrega el Método Escala?
            </h3>
            <div className="overflow-x-auto" data-id="element-160">
              <table className="w-full text-left border-collapse bg-white rounded-2xl overflow-hidden shadow-sm border border-[#FADCE2]" data-id="element-161">
                <thead data-id="element-162">
                  <tr className="bg-[#0A0A0A] text-[#FADCE2]" data-id="element-163">
                    <th className="p-4 md:p-6 font-semibold w-1/3" data-id="element-164">
                      Situación
                    </th>
                    <th className="p-4 md:p-6 font-semibold w-1/3 border-l border-[#E89BAA]/20" data-id="element-165">
                      ❌ Sin Método Escala
                    </th>
                    <th className="p-4 md:p-6 font-semibold w-1/3 border-l border-[#E89BAA]/20 bg-[#111]" data-id="element-166">
                      ✅ Con Método Escala
                    </th>
                  </tr>
                </thead>
                <tbody className="text-[#0A0A0A]" data-id="element-167">
                  {[['Agenda llena pero sin poder ganar más', 'Seguís igual', 'Optimizás tiempo y subís ticket'], ['Querés cobrar más pero tenés miedo', 'Seguís cobrando de menos', 'Estrategia de subida sin perder clientas'], ['No podés tomarte un día libre', 'El negocio para cuando vos parás', 'Sistema de equipo que sostiene'], ['Solo cobrás cuando trabajás', 'Sin ingreso pasivo', 'Retail, membresías y cursos propios']].map((row, i) => <tr key={i} className="border-b border-[#FADCE2] last:border-0" data-id="element-168">
                      <td className="p-4 md:p-6 font-medium bg-[#FFF8FA]/50" data-id="element-169">
                        {row[0]}
                      </td>
                      <td className="p-4 md:p-6 text-[#0A0A0A]/70 border-l border-[#FADCE2]" data-id="element-170">
                        {row[1]}
                      </td>
                      <td className="p-4 md:p-6 font-semibold text-[#0A0A0A] border-l border-[#FADCE2] bg-[#FADCE2]/10" data-id="element-171">
                        {row[2]}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* MORE TESTIMONIALS */}
      <section className="py-20 px-4 bg-[#0A0A0A] text-[#FFF8FA]" data-id="element-172">
        <div className="max-w-6xl mx-auto" data-id="element-173">
          <FadeIn data-id="element-174">
            <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-center mb-12" data-id="element-175">
              ❤️ MÁS MANICURAS QUE YA LO TIENEN
            </h2>
          </FadeIn>
          <div className="grid md:grid-cols-3 gap-6" data-id="element-176">
            {[{
            name: 'Cecilia R.',
            loc: 'Mendoza',
            text: 'El módulo de membresías me cambió la vida. A principio de mes ya sé cuánto voy a ganar.'
          }, {
            name: 'Sofía C.',
            loc: 'La Plata',
            text: 'Mis clientas empezaron a volver solas. Nunca más tuve que salir a buscarlas.'
          }, {
            name: 'Marcela T.',
            loc: 'Neuquén',
            text: 'El Tomo de Redes me ahorró horas por semana. Publico sin estrés desde hace meses.'
          }].map((t, i) => <FadeIn key={i} delay={i * 0.1} data-id="element-177">
                <div className="bg-[#111] p-8 rounded-3xl border border-[#E89BAA]/20 h-full" data-id="element-178">
                  <div className="flex gap-1 mb-4" data-id="element-179">
                    {[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4 fill-[#E89BAA] text-[#E89BAA]" data-id="element-180" />)}
                  </div>
                  <p className="text-lg font-light text-[#FFF8FA]/90 mb-6" data-id="element-181">
                    "{t.text}"
                  </p>
                  <p className="font-semibold text-[#FADCE2]" data-id="element-182">
                    — {t.name} ·{' '}
                    <span className="text-[#FFF8FA]/50 font-normal" data-id="element-183">
                      📍 {t.loc}
                    </span>
                  </p>
                </div>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* GUARANTEE */}
      <section className="py-20 px-4 bg-[#FADCE2] text-[#0A0A0A]" data-id="element-184">
        <div className="max-w-3xl mx-auto text-center" data-id="element-185">
          <FadeIn data-id="element-186">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-[#0A0A0A] rounded-full mb-8 shadow-xl" data-id="element-187">
              <Shield className="w-10 h-10 text-[#FADCE2]" data-id="element-188" />
            </div>
            <h2 className="font-['Playfair_Display'] text-3xl md:text-5xl font-bold mb-6" data-id="element-189">
              GARANTÍA 7 DÍAS — SIN PREGUNTAS
            </h2>
            <p className="text-lg md:text-xl mb-6 font-medium" data-id="element-190">
              Abrís los tomos. Los usás en tu negocio. Si en 7 días no es lo que
              esperabas: devolución del 100% sin formularios, sin drama.
            </p>
            <p className="text-[#0A0A0A]/60 font-semibold uppercase tracking-widest text-sm" data-id="element-191">
              Tasa de devoluciones: menos del 1%.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* COST OF INACTION */}
      <section className="py-24 px-4 bg-[#FFF8FA]" data-id="element-192">
        <div className="max-w-4xl mx-auto" data-id="element-193">
          <FadeIn data-id="element-194">
            <h2 className="font-['Playfair_Display'] text-4xl font-bold text-center text-[#0A0A0A] mb-12" data-id="element-195">
              ⚖️ ¿CUÁNTO CUESTA NO TENERLO?
            </h2>
            <div className="bg-white rounded-3xl shadow-sm border border-[#FADCE2] overflow-hidden" data-id="element-196">
              <table className="w-full text-left border-collapse" data-id="element-197">
                <thead data-id="element-198">
                  <tr className="bg-[#FFF8FA] border-b border-[#FADCE2]" data-id="element-199">
                    <th className="p-5 md:p-6 font-semibold text-[#0A0A0A]" data-id="element-200">
                      Sin Megapack
                    </th>
                    <th className="p-5 md:p-6 font-semibold text-[#E89BAA] border-l border-[#FADCE2]" data-id="element-201">
                      Costo real
                    </th>
                  </tr>
                </thead>
                <tbody className="text-[#0A0A0A]" data-id="element-202">
                  {[['Agenda floja 2–3 semanas por mes', '$60.000–$120.000 perdidos mensual'], ['Precios a ojo', 'Regalás trabajo sin saberlo'], ['Sin sistema de ventas', 'El 50% de consultas no se convierten'], ['Sin protocolo de atención', 'Las clientas se van sin avisarte'], ['Sin plan de redes', 'Horas perdidas sin resultados']].map((row, i) => <tr key={i} className="border-b border-[#FADCE2]/50 last:border-0" data-id="element-203">
                      <td className="p-5 md:p-6 font-medium" data-id="element-204">{row[0]}</td>
                      <td className="p-5 md:p-6 text-[#0A0A0A]/70 border-l border-[#FADCE2]/50" data-id="element-205">
                        {row[1]}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 px-4 bg-[#FFF8FA] border-t border-[#FADCE2]" data-id="element-206">
        <div className="max-w-3xl mx-auto" data-id="element-207">
          <FadeIn data-id="element-208">
            <h2 className="font-['Playfair_Display'] text-4xl font-bold text-center text-[#0A0A0A] mb-12" data-id="element-209">
              ❓ PREGUNTAS FRECUENTES
            </h2>
            <div className="bg-white p-6 md:p-10 rounded-3xl shadow-sm border border-[#FADCE2]" data-id="element-210">
              {[{
              q: '¿Sirve si recién empiezo?',
              a: 'Sí. Está diseñado para funcionar desde el día 1, con o sin experiencia previa en gestión.'
            }, {
              q: '¿Cómo lo recibo?',
              a: 'Por email, inmediatamente después del pago. Acceso en menos de 5 minutos desde tu celular o computadora.'
            }, {
              q: '¿Qué lo diferencia del material gratuito?',
              a: 'Está construido específicamente para manicuras argentinas. Precios en pesos, ejemplos reales del mercado local y lenguaje adaptado a cómo trabajás vos.'
            }, {
              q: '¿Funciona si trabajo desde casa?',
              a: 'Perfectamente. El sistema aplica a cualquier modalidad: casa, domicilio o local.'
            }, {
              q: '¿Tiene garantía real?',
              a: 'Sí. 7 días. Si no es lo que esperabas, escribinos y te devolvemos el 100%. Sin preguntas.'
            }].map((faq, i) => <FaqItem key={i} question={faq.q} answer={faq.a} data-id="element-211" />)}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-24 px-4 bg-[#0A0A0A] text-[#FFF8FA]" data-id="element-212">
        <div className="max-w-4xl mx-auto text-center" data-id="element-213">
          <FadeIn data-id="element-214">
            <div className="flex items-center justify-center gap-2 text-[#E89BAA] font-bold tracking-widest uppercase mb-6" data-id="element-215">
              <Clock className="w-5 h-5" data-id="element-216" /> EL PRECIO CIERRA EN:
            </div>
            <CountdownTimer data-id="element-217" />

            <div className="grid md:grid-cols-2 gap-6 mt-16 text-left" data-id="element-218">
              <div className="bg-[#111] border border-[#E89BAA]/30 rounded-2xl p-6 md:p-8" data-id="element-219">
                <h3 className="text-xl font-bold text-[#FADCE2] mb-4" data-id="element-220">
                  📘 MEGAPACK MANICURA LISTO
                </h3>
                <div className="flex items-baseline gap-2 mb-6" data-id="element-221">
                  <span className="text-[#FFF8FA]/40 line-through text-lg" data-id="element-222">
                    $51.000
                  </span>
                  <span className="text-3xl font-bold text-[#FFF8FA]" data-id="element-223">
                    $19.990
                  </span>
                  <span className="text-sm font-bold text-[#E89BAA]" data-id="element-224">ARS</span>
                </div>
                <a href={CHECKOUT_BASE} target="_blank" rel="noopener noreferrer" className="w-full bg-[#FADCE2] text-[#0A0A0A] py-3 rounded-xl font-bold hover:bg-white transition-colors flex items-center justify-center" data-id="element-225">
                  → QUIERO MI MEGAPACK
                </a>
              </div>

              <div className="bg-[#111] border-2 border-[#E89BAA] rounded-2xl p-6 md:p-8 relative" data-id="element-226">
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#E89BAA] text-[#0A0A0A] px-4 py-1 rounded-full text-xs font-bold flex items-center gap-1 whitespace-nowrap" data-id="element-227">
                  <Star className="w-3 h-3 fill-current" data-id="element-228" /> MÁS ELEGIDO
                </div>
                <h3 className="text-xl font-bold text-[#FADCE2] mb-4" data-id="element-229">
                  🏆 MEGAPACK + MÉTODO ESCALA
                </h3>
                <div className="flex items-baseline gap-2 mb-6" data-id="element-230">
                  <span className="text-[#FFF8FA]/40 line-through text-lg" data-id="element-231">
                    $69.000
                  </span>
                  <span className="text-3xl font-bold text-[#FFF8FA]" data-id="element-232">
                    $27.990
                  </span>
                  <span className="text-sm font-bold text-[#E89BAA]" data-id="element-233">ARS</span>
                </div>
                <a href={CHECKOUT_ESCALA} target="_blank" rel="noopener noreferrer" className="w-full bg-[#E89BAA] text-[#0A0A0A] py-3 rounded-xl font-bold hover:bg-[#FADCE2] transition-colors shadow-[0_0_15px_rgba(232,155,170,0.3)] flex items-center justify-center" data-id="element-234">
                  → QUIERO EL MEGAPACK + MÉTODO ESCALA
                </a>
              </div>
            </div>

            <div className="mt-12 flex flex-wrap justify-center items-center gap-6 text-sm text-[#FFF8FA]/50" data-id="element-235">
              <span className="flex items-center gap-2" data-id="element-236">
                <Lock className="w-4 h-4" data-id="element-237" /> Pago seguro
              </span>
              <span className="flex items-center gap-2" data-id="element-238">
                <Check className="w-4 h-4" data-id="element-239" /> Acceso en 5 min
              </span>
              <span className="flex items-center gap-2" data-id="element-240">
                <Shield className="w-4 h-4" data-id="element-241" /> Garantía 7 días
              </span>
              <span className="flex items-center gap-2" data-id="element-242">
                <CreditCard className="w-4 h-4" data-id="element-243" /> Mercado Pago
              </span>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-[#050505] text-[#FFF8FA]/60 py-16 px-4 text-center border-t border-[#111]" data-id="element-244">
        <div className="max-w-4xl mx-auto" data-id="element-245">
          <p className="text-[#E89BAA] font-bold tracking-widest uppercase text-lg mb-4" data-id="element-246">
            💅 AURA BIT™
          </p>
          <p className="mb-8 max-w-md mx-auto" data-id="element-247">
            El sistema N°1 para manicuras emprendedoras de Argentina. Porque
            tener un negocio de manicura no debería costarte todos los domingos
            del año.
          </p>
          <div className="flex flex-col md:flex-row justify-center items-center gap-4 md:gap-8 mb-12 text-sm" data-id="element-248">
            <a href="mailto:soportebibliotecad@gmail.com" className="hover:text-[#FADCE2] transition-colors" data-id="element-249">
              📧 soportebibliotecad@gmail.com
            </a>
            <a href="https://www.bibliotecadigital.com" className="hover:text-[#FADCE2] transition-colors" data-id="element-250">
              🌐 www.bibliotecadigital.com
            </a>
          </div>
          <p className="text-xs mb-8" data-id="element-251">
            © Aura Bit™ 2026 — Todos los derechos reservados. Prohibida la
            reproducción y redistribución sin autorización expresa.
          </p>
          <p className="font-['Playfair_Display'] italic text-xl text-[#FADCE2]" data-id="element-252">
            💅 "El próximo lunes puede ser completamente diferente."
          </p>
        </div>
      </footer>
    </div>;
}